<template>
  <div>
    <b-row
      v-if="!isGraphic"
      class="mt-3"
    >
      <b-col cols="12">
        <b-checkbox
          class="d-inline"
          v-model="pagedFilter.onlyAlarmed"
          @change.native="getListData"
        >
          <span class="font-weight-normal">
            {{ $t('engineering.load-simulation.only-alarmed') }}
          </span>
        </b-checkbox>
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col
        md="4"
      >
        <span class="load-simulation__green">
          {{ $t('reports.load-simulation.simulation-total-loss-life') }}:
        </span>
        <span class="font-weight-bold">{{ simulationTotalLossLife }}</span>
      </b-col>
      <b-col
        class="load-simulation__align-items--center"
        md="4"
      >
        <div>
          <span class="load-simulation__green">
          {{ $t('reports.load-simulation.remaining-life-after-simulation') }}:
          </span>
          <span class="font-weight-bold">{{ remainingLifeAfterSimulation }}</span>
        </div>
      </b-col>
      <b-col
        class="load-simulation__tabs"
        md="4"
      >
        <div>
          <b-form-radio-group
            buttons
            button-variant="outline-primary"
            size="sm"
            :checked="reportType"
            :value="reportType"
            :options="reportTypes"
            @input="updateReportType"/>
        </div>
      </b-col>
    </b-row>
    <div class="mt-3 load-simulation__border">
      <b-row>
        <b-col
          lg="12"
          align-h="center">
          <div class="p-3 ">
            <div class="load-simulation__chart">
              <div v-if="isGraphic">
                <default-chart
                  v-if="chartData.length > 0"
                  class="my-2"
                  x-data-manager="dateTime"
                  legend-icon="rect"
                  chart-id="load-simulation"
                  :chartData="chartData"
                  :loading="loadingGraphic"
                  :remove-outlier.sync="removeChartOutlier"
                  :enable-multi-axis.sync="enableChartMultiAxis"
                  @toggle-outlier="getChartData"
                  @toggle-multi-axis="getChartData"
                />
                <s-empty-list
                  v-else
                  class="w-100"
                />
              </div>
              <div v-else>
                <load-simulation-result-list
                  class="load-simulation__result-list"
                  :pagination="pagedFilter"
                  :reports="listReport"
                  :loading="loadingInfo"
                  @page-changed="pageChange"
                />
              </div>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>
<script>
import eventBus from '@/services/globalEventBus';
import events from '@/domain/events/engineering';
import DefaultChart from '@/components/charts/DefaultChart';
import defaultChartMixin from '@/domain/mixins/defaultChartMixin';
import GraphicReport from '@/domain/components/generalPanel/reports/loadSimulation/GraphicReport';
import InfoReport from '@/domain/components/generalPanel/reports/loadSimulation/InfoReport';
import loadSimulationService
  from '@/domain/services/installation/engineering/loadSimulation/loadSimulationService';
import LoadSimulationResultList
  from '@/domain/components/installation/engineering/loadSimulation/reports/List';

const reportType = {
  graphic: 0,
  info: 1,
};

export default {
  name: 'LoadSimulationChart',
  props: {
    simulationTotalLossLife: {
      type: [Number, String],
      default: () => (0),
    },
    remainingLifeAfterSimulation: {
      type: Number,
      default: () => (0),
    },
  },
  components: {
    DefaultChart,
    LoadSimulationResultList,
  },
  data: () => ({
    reportType: reportType.graphic,
    chartData: [],
    listReport: [],
    loadingGraphic: false,
    loadingInfo: false,
    pagedFilter: {
      pageSize: 50,
      page: 1,
      totalRows: null,
      onlyAlarmed: false,
    },
  }),
  mixins: [
    defaultChartMixin,
  ],
  computed: {
    reportTypes() {
      return [
        {
          text: this.$t('reports.load-simulation.view-simulation.graphic'),
          value: reportType.graphic,
          component: GraphicReport,
        },
        {
          text: this.$t('reports.load-simulation.view-simulation.info'),
          value: reportType.info,
          component: InfoReport,
        },
      ];
    },
    isGraphic() {
      return this.reportType === reportType.graphic;
    },
  },
  created() {
    this.initListener();
  },
  beforeDestroy() {
    this.removeListener();
  },
  methods: {
    pageChange(page) {
      this.pagedFilter.page = page;
      this.getListData();
    },
    initListener() {
      eventBus.on(events.onSimulationHeaderSuccess, this.loadSimulationData, this);
    },
    removeListener() {
      eventBus.off(events.onSimulationHeaderSuccess, this.loadSimulationData, this);
    },
    updateReportType(reportTypeSelected) {
      this.reportType = reportTypeSelected;
    },
    loadSimulationData() {
      this.getChartData();
      this.getListData();
    },
    getListData() {
      const { assetModuleId, resultId } = this.$route.params;
      this.loadingInfo = true;
      loadSimulationService.getPaginatedResult(
        assetModuleId,
        resultId,
        this.pagedFilter,
      ).then((result) => {
        this.listReport = result.data;
        this.pagedFilter.totalRows = result.total;
        this.pagedFilter.page = result.page;
      })
        .finally(() => {
          this.loadingInfo = false;
        });
    },
    getChartData() {
      this.loadingGraphic = true;
      const { assetModuleId, resultId } = this.$route.params;
      loadSimulationService.getResultChart(
        assetModuleId,
        resultId,
        this.chartFilters,
      ).then((data) => {
        this.chartData = data;
      })
        .finally(() => {
          this.loadingGraphic = false;
        });
    },
  },
};
</script>
<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

::v-deep
  .e-chart
    border: none

.load-simulation
  &__title
    font-weight: bold
    margin-left: 30px
    font-size: 1.1rem

  &__green
    color: var(--color-success)
    font-weight: bold

  &__border
    border: 2px solid #bdbdbd52
    border-radius: 5px

  &__tabs
    display: flex
    justify-content: flex-end

  &__align-items--center
    display: flex
    flex-direction: row
    justify-content: center

  +media-max-sm()
    &__align-items--center
      display: block
      margin-bottom: 1.5em

</style>
